// For polyfilling:
import 'core-js'
import 'regenerator-runtime/runtime.js'
import React, { StrictMode } from 'react'
import { HelmetProvider } from 'react-helmet-async'

import { ApolloProvider } from '@apollo/client'
import * as Sentry from '@sentry/react'
import { checkLocale } from '@toasttab/buffet-pui-locale-utilities'
import { initI18Next } from '@toasttab/buffet-pui-translation-utilities'
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk'
import { hydrateRoot } from 'react-dom/client'

import {
  BATCHING_ENABLED_DEFAULTS,
  createClient
} from 'src/apollo/createClient'
import { loadTranslations } from 'src/translations/src/utilities'

import ErrorBoundary from 'shared/components/common/error_boundary/ErrorBoundary'

import ClientApp from 'public/components/app/ClientApp'

import { sentry, resources, ldClientId } from 'config'

import './index.scss'

// Kick off client-side monitoring
if (sentry.dsn && typeof window !== 'undefined') {
  Sentry.init({
    dsn: sentry.dsn,
    environment: sentry.env,
    // eslint-disable-next-line no-undef
    release: `toast-sites-web@${VERSION}`,
    // don't log errors from PetalBot
    beforeSend: (ev) =>
      ev?.request?.headers['User-Agent'].includes('PetalBot') ? null : ev
  })
}

const renderApp = async () => {
  const windowDefined = typeof window !== 'undefined'
  const i18nextState = windowDefined ? window.__I18NEXT_STATE__ : undefined
  //checkLocale ensures default to en-US if i18nextState is undefined
  initI18Next(checkLocale(i18nextState?.language))
  loadTranslations()

  const LDProvider = await asyncWithLDProvider({
    clientSideID: ldClientId,
    context: windowDefined ? window.__FLAGS_CONTEXT__ : { key: 'sites-web' },
    options: {
      streaming: false,
      sendEvents: false,
      bootstrap: windowDefined ? window.__FLAGS_STATE__ : undefined
    }
  })

  const component = (
    <StrictMode>
      <ErrorBoundary>
        <LDProvider>
          <ApolloProvider
            client={createClient({
              uri: resources.apiEndpoint,
              state: windowDefined ? window.__APOLLO_STATE__ : undefined,
              batchingOptions: BATCHING_ENABLED_DEFAULTS,
              timeout: resources.clientQueryTimeoutMs
            })}
          >
            <HelmetProvider>
              <ClientApp />
            </HelmetProvider>
          </ApolloProvider>
        </LDProvider>
      </ErrorBoundary>
    </StrictMode>
  )

  const container = document.getElementById('content')

  hydrateRoot(container, component)
}

renderApp()
